"use client";
import React, { useState, useMemo, useEffect } from "react";
import Image from "next/image";
import { Link } from 'next-view-transitions'
import { usePathname } from "next/navigation";
import cn from "classnames";
import Styles from "./nav.module.scss";

export type NavItem = {
  id: string;
  label: string;
  href: string;
  enabled?: boolean;
};

export type NavProps = {
  items: NavItem[];
  className?: string;
  ariaLabel?: string;
};

const Nav: React.FC<NavProps> = ({ items, className, ariaLabel = "Main navigation" }) => {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const enabledItems = useMemo<NavItem[]>(() => items.filter((it) => it.enabled !== false), [items]);

  useEffect(() => {
    setOpen(false);
  }, [pathname])

  if (enabledItems.length === 0) return null;

  const toggle = () => setOpen((s) => !s);

  const renderItem = (item: NavItem, index: number) => {
    const { href, label, id, enabled } = item;

    if (!enabled) return null;

    const isActive = typeof pathname === 'string' && pathname.replace(/(?<=.)\/$/, '') === href;
    return (
      <li key={id ?? `${label}-${index}`} className={cn(Styles.item, { [Styles.active]: isActive })}>
        {isActive
          ? label
          : <Link href={href} aria-label={label} className={Styles.link}>
              {label}
            </Link>
        }
      </li>
    );
  };

  return (
    <nav className={cn(Styles.nav, className, { [Styles.open]: open })} aria-label={ariaLabel}>
      <Link
        href="/"
        aria-label="Home"
        className={cn(Styles.bearFrame, { [Styles.disable]: pathname === '/' })}
      >
        <Image
          className={Styles.bear}
          src="/surfing-bear.webp"
          alt="The California Roadhouse"
          sizes="(max-width: 768px) 50vw, 25vw"
          width={332}
          height={332}
          priority
          unoptimized={true}
        />
      </Link>
      <a className={cn(Styles.toggle, { [Styles.open]: open })} onClick={toggle} aria-expanded={open} aria-controls="site-nav">
        ☰
      </a>
      <ul id="site-nav" className={cn(Styles.list)}>
        {enabledItems.map(renderItem)}
      </ul>
    </nav>
  );
};

export default Nav;
